/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { Divider, Typography } from '@material-ui/core';
import { isEmpty } from 'lodash';
import { Element } from 'react-scroll';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { Theme as MaterialUITheme } from '@rjsf/material-ui';
import withTheme from './rjsf/withTheme';
import { fields, templates, widgets } from './templates';
import SummarySelector from '../summary/SummarySelector';
import { handleBusinessRuleValidations, postProcess } from '../../shared/configuration/businessRules';
import LoadingPreview from './LoadingPreview';
import WelcomeSection from '../welcomeSection/WelcomeSection';
import useProcedure from '../../procedure/useProcedure';
import { SubmissionContextProvider } from './context/SubmissionContextProvider';
import { handleCancelError } from '../../api/helpers/api.helpers';

const MuiForm = withTheme(MaterialUITheme);

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
  },
  heading: {
    marginBottom: '4rem',
  },
  title: {
    marginBottom: '1rem',
  },
  description: {
    fontWeight: 400,
    marginBottom: '1.5rem',
  },
  paper: {
    margin: '1.5rem 0',
    padding: '1rem 1rem',
    backgroundColor: theme.palette.elementBackground,
    boxShadow: 'none',
    borderRadius: '1rem',

    [theme.breakpoints.up('md')]: {
      padding: '2rem 3rem',
    },
    [theme.breakpoints.up('xl2')]: {
      width: '75%',
      maxWidth: '60rem',
    },
  },
  whitePaper: {
    marginBottom: '4rem',
    padding: '1rem 1rem',
    backgroundColor: theme.palette.white,
    boxShadow: '0rem 0.0625rem 0.1875rem rgba(63, 63, 68, 0.15)',
    borderRadius: '0.25rem',
    [theme.breakpoints.up('md')]: {
      padding: '2rem 3rem',
    },
    [theme.breakpoints.up('xl2')]: {
      width: '75%',
      maxWidth: '60rem',
    },
  },
  sectionDivider: {
    borderBottom: '0.1rem solid grey',
    marginBottom: theme.spacing(2),
  },
  sectionTopIndent: {
    paddingBottom: theme.spacing(1),
  },
  customTooltip: {
    fontSize: '0.9',
    fontWeight: 500,
  },
  lastSectionIndent: {
    marginBottom: theme.spacing(10),
  },
  sectionBottomIndent: {
    marginBottom: theme.spacing(2),
  },
  actionButton: {
    marginRight: theme.spacing(1),
  },
  bottomBar: {
    backgroundColor: 'rgba(0,0,0,0.1)',
    top: 'auto',
    bottom: 0,
  },
  textField: {
    minWidth: '14rem',
    [theme.breakpoints.up('md')]: {
      minWidth: '19rem',
    },
  },
}));

function Submission({
  readOnly = false,
  validationSchema,
  uiSchema,
  setUiSchema,
  submission,
  setSubmission,
  // TODO: evaluate if extra errors are still used
  extraErrors,
  setExtraErrors,
  submissionId,
  dispatchDraftState,
  setWorkflowValidations,
}) {
  const idPrefix = 'submission';
  const classes = useStyles();
  const { t } = useTranslation();
  const procedure = useProcedure();
  const hideDescription = uiSchema?.basicFilingInfo?.['ui:options']?.hideDescription;

  const welcomeSectionProps = {
    title: submission?.requestInformation?.requestType
      ? t(submission?.requestInformation?.requestType)
      : t(`draft.${procedure.id}.title`),
    extraTitle: submission?.requestInformation?.categoryOfRequest
      ? t(submission?.requestInformation?.categoryOfRequest)
      : undefined,
    description: hideDescription ? undefined : t(`draft.${procedure.id}.description`),
  };

  const onChange = async (props, signal, doPostProcess = false, event = null, propertyId = null) => {
    try {
      const { formData } = props;
      let newSubmission = { ...formData, id: submissionId };
      if (doPostProcess) {
        newSubmission = await postProcess(newSubmission, event, propertyId, signal);
      }
      const {
        workflowValidations: newWorkflowValidations,
        uiSchema: newUiSchema,
      } = await handleBusinessRuleValidations(newSubmission, uiSchema, null, null, signal);

      setWorkflowValidations(newWorkflowValidations);
      setExtraErrors(newWorkflowValidations.extraErrors);
      setUiSchema(newUiSchema);
      setSubmission(newSubmission);
    } catch (error) {
      handleCancelError(error);
    }
  };

  const onSectionChange = async (props, signal) => {
    const { formData, changeProps } = props;
    if (changeProps?.sectionName) {
      // section level change
      if (!changeProps?.sectionItemId) {
        // non submitted entity canceled (no postprocessing here)
        setSubmission(formData);
        return;
      }
      const { sectionName, sectionOperation, sectionItemId } = changeProps;
      const event = `${sectionName}_${sectionOperation}`;
      await onChange(props, signal, true, event, sectionItemId);
    } else {
      // field level change
      const { propertyId } = changeProps;
      const event = `${propertyId}_change`;
      await onChange(props, signal, true, event, propertyId);
    }
  };

  return (
    <>
      <div className={classes.heading}>
        <WelcomeSection {...welcomeSectionProps} contrast="true" />
        <Typography variant="h4" component="h2">
          <strong>{t('draft.np.note')}</strong>
        </Typography>
      </div>
      {!isEmpty(validationSchema) && !isEmpty(uiSchema) && !isEmpty(submission) ? (
        <SubmissionContextProvider>
          <MuiForm
            translation={t}
            className={classes.form}
            idPrefix={idPrefix}
            id={idPrefix}
            schema={validationSchema}
            uiSchema={uiSchema}
            fields={fields}
            widgets={widgets}
            templates={templates}
            formData={submission}
            focusOnFirstError={true}
            formContext={{
              classes,
              dispatchDraftState,
              submissionId,
              attachments: submission?.attachments,
              schema: validationSchema,
              submission,
            }}
            onChange={!readOnly && onChange}
            onBlur={!readOnly && onChange}
            onSectionChange={onSectionChange}
            showErrorList={false}
            disabled={readOnly}
            extraErrors={extraErrors}
          >
            <Element name="summary" className={classes.lastSectionIndent}>
              <Typography variant="h2" className={classes.sectionTopIndent}>
                {t('summary')}
              </Typography>
              <Divider className={classes.sectionDivider} />
              <SummarySelector
                submission={submission}
                classes={classes}
                uiSchema={uiSchema || {}}
                readOnly={readOnly || submissionId == null}
                onSectionChange={onSectionChange}
                jsonSchema={validationSchema}
              />
            </Element>
          </MuiForm>
        </SubmissionContextProvider>
      ) : (
        <LoadingPreview />
      )}
    </>
  );
}

export default React.memo(Submission);
